import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import * as amplitude from "@amplitude/analytics-browser";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";
import type { ICreateGatewayPayload, IOption } from "@app/dashboard/types/innerLayout/payment_integrations/create-integrations";
import { Breadcrumbs, Button } from "@unified-checkout/design-system";
import { Plus } from "@unified-checkout/design-system/icons/index";
import { PAYMENT_INTEGRATIONS_EVENTS, PAYMENT_INTEGRATIONS_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/payment-integrations";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { createPaymentIntegration } from "@app/dashboard/services/inner_layout/payment_integrations/create";
import { getAllowedIntegrations } from "@app/dashboard/services/inner_layout/payment_integrations/get-allowed-integrations";
import { CreateIntegrationModal } from "../components/modals/CreationModal/modal/CreateIntegrationModal";
import { useSettingsUtils } from "../../Settings/useSettingsUtils";
import { getCurrentMode } from "@app/dashboard/utils/getCurrentMode";
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import styles from "./header.module.scss";

const SHOPIFY = 'shopify'

export const Header = () => {
  const { t } = useTranslation();
  const { internalPagesBreadcrumbs } = useSettingsUtils();

  const wrapperRef = useRef<any>();
  const { isLive } = useTestLiveModeStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<string | undefined>();
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>();
  const [isShopify, setIsShopify] = useState<boolean>(false);
  const [processedCallback, setProcessedCallback] = useState<string>("");
  const [responseCallback, setResponseCallback] = useState<string>("");
  const [response, setresponse] = useState<boolean>(false);
  const [responseStatus, setResponseStatus] = useState<'success' | 'error' | undefined>();
  const [responseMessage, setResponseMessage] = useState<string | undefined>(undefined);
  
  const canCreatePaymentIntegrations = isAccessible(PERMISSIONS.CAN_CREATE_PAYMENT_INTEGRATIONS);

  const {
    mutate: getAllowedIntegrationsMutate,
    data: creationListResponse,
  } = useMutation({
    mutationKey: [QUERY_KEYS.GET_ALLOWED_INTEGRATIONS],
    mutationFn: getAllowedIntegrations,
  });

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    getAllowedIntegrationsMutate();
  };

  const {
    mutate: createIntegrationMutate,
    isPending: createIntegrationPending,
    reset: createIntegrationReset
  } = useMutation({
    mutationKey: [QUERY_KEYS.ADD_PAYMENT_INTEGRATION],
    mutationFn: (integrationData: ICreateGatewayPayload) => createPaymentIntegration(integrationData),
    onSuccess: (_, integrationData) => {
      clearForm();
      setResponseStatus('success');
      setResponseMessage(undefined);
      setresponse(true);
      amplitude.track(PAYMENT_INTEGRATIONS_EVENTS.CREATE_PAYMENT_INTAGRATIONS, {
        [SHARED_EVENTS_PARAMS.ENVIRONMENT]: getCurrentMode(isLive),
        [PAYMENT_INTEGRATIONS_EVENTS_PARAMS.EVENTS_SUBSCRIBED_COUNT]: getEnabledWebhookEventTypesCount(
          integrationData
        ),
        [SHARED_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_PARAMS.SUCCESS
      });
    },
    onError: (error: any, integrationData) => {
      setresponse(true);
      setResponseStatus('error');
      const messageFromData = error.response.data.message
      setResponseMessage(messageFromData ? messageFromData : undefined);
      amplitude.track(PAYMENT_INTEGRATIONS_EVENTS.CREATE_PAYMENT_INTAGRATIONS, {
        [SHARED_EVENTS_PARAMS.ENVIRONMENT]: getCurrentMode(isLive),
        [PAYMENT_INTEGRATIONS_EVENTS_PARAMS.EVENTS_SUBSCRIBED_COUNT]: getEnabledWebhookEventTypesCount(
          integrationData
        ),
        [SHARED_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_PARAMS.FAILED
      });
    }
  })


  const allowedCountryGateways = (creationListResponse?.data)?.allowed_country_gateways || {};

  const handleCreateModalCancel = () => {
    setShowCreateModal(false);
    clearForm();
    setresponse(false);
    setResponseStatus(undefined);
  };

  const handleSubmitIntegration = () => {
    createIntegrationMutate({
      gateway_type: selectedPaymentType!,
      currency: selectedCurrency!,
      is_shopify: isShopify,
      ...(processedCallback && {
        transaction_processed_callback: processedCallback,
      }),
      ...(responseCallback && {
        transaction_response_callback: responseCallback,
      }),
    });
  };
  const clearForm = () => {
    setSelectedPaymentType(undefined);
    setSelectedCurrency(undefined);
    setIsShopify(false);
    setProcessedCallback('');
    setResponseCallback('');
    createIntegrationReset();
  }
  const getAvailableCurrencies = () => {
    if (!selectedPaymentType) return [];
    const gateway = allowedCountryGateways?.[selectedPaymentType];
    const currencies = gateway?.currencies || [];
    return currencies.map((currency: IOption) => ({
      label: currency,
      value: currency,
    }));
  };

  const getEnabledWebhookEventTypesCount = (integrationData: ICreateGatewayPayload) => {
    let count = 0;

    if (integrationData.transaction_processed_callback?.trim()) count += 1;
    if (integrationData.transaction_response_callback?.trim()) count += 1;
    if (integrationData.currency?.trim()) count += 1;
    if (integrationData.gateway_type?.trim()) count += 1;
    if (typeof integrationData.is_shopify === 'boolean') count += 1;

    return count;
  };

  const getPaymentTypeOptions = () => {
    return Object.keys(allowedCountryGateways || {}).map((gateway) => ({
      label: gateway,
      value: gateway,
    }));
  };

  const handlePaymentTypeChange = (value: string) => {
    setSelectedPaymentType(value);
    setSelectedCurrency(undefined);
  };

  const handlePlatformChange = (value: string) => {
    setIsShopify(value === SHOPIFY);
  };

  const createIntegrationModalProps = {
    open: showCreateModal,
    selectedPaymentType,
    selectedCurrency,
    isShopify,
    processedCallback,
    responseCallback,
    createIntegrationPending,
    allowedCountryGateways,
    response,
    responseStatus,
    responseMessage,
    onSubmit: handleSubmitIntegration,
    onClose: handleCreateModalCancel,
    onResponseClose: () => {
      setShowCreateModal(false);
      setresponse(false);
      setResponseStatus(undefined);
    },
    getPaymentTypeOptions,
    getAvailableCurrencies,
    onPlatformChange: handlePlatformChange,
    onPaymentTypeChange: handlePaymentTypeChange,
    onCurrencyChange: (value: string) => setSelectedCurrency(value),
    onProcessedCallbackChange: (value: string) => setProcessedCallback(value),
    onResponseCallbackChange: (value: string) => setResponseCallback(value),
  };
  return (
    <div className={styles["header"]} ref={wrapperRef}>
      <div className={styles["header__breadcrumbs"]}>
        <Breadcrumbs
          linksProps={internalPagesBreadcrumbs.PAYMENT_INTEGRATIONS}
        />
      </div>
      <div className={styles["header__titles"]}>
        <div className={styles["header__title"]}>
          {t("innerLayout.paymentIntegrationsPage.integrationsWebhooksTitle")}
        </div>

        <div className={styles["header__subtitle"]}>
          {t("innerLayout.paymentIntegrationsPage.integrationsWebhooksSubtitle")}
        </div>
      </div>
      {(!isLive && canCreatePaymentIntegrations ) &&
        <div>
          <Button
            size="md"
            color="primary"
            variant="contained"
            leadingIcon={Plus}
            text={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.createIntegrations')}
            onClickHandler={handleOpenCreateModal}
          />
        </div>
      }
      <div className={styles["header__creation-modal"]}>
        <CreateIntegrationModal {...createIntegrationModalProps} />
      </div>
    </div>
  );
}